import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { Input } from "@shared/components/ui/input";
import { Button } from "@shared/components/ui/button";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Eye, EyeOff, KeyRound, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useChangeUserPassword } from "./useUsers";
import { toast } from "sonner";
import { USER_VALIDATION, USER_FORM_FIELDS } from "./constants";
import { cn } from "@/lib/utils";

const passwordSchema = z
  .object({
    password: z
      .string()
      .min(
        USER_VALIDATION.PASSWORD_MIN_LENGTH,
        "Password must be at least 8 characters"
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const ChangePasswordDialog = ({ user, open, onOpenChange }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { mutate: changePassword, isPending } = useChangeUserPassword();
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
  });

  const form = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = form.watch("password");
  const confirmPassword = form.watch("confirmPassword");

  // Calculate password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength({ score: 0, label: "", color: "" });
      return;
    }

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    score = Object.values(checks).filter(Boolean).length;

    const strengthMap = {
      0: { label: "", color: "" },
      1: { label: t("user.passwordWeak"), color: "text-red-600" },
      2: { label: t("user.passwordWeak"), color: "text-red-600" },
      3: { label: t("user.passwordMedium"), color: "text-yellow-600" },
      4: { label: t("user.passwordStrong"), color: "text-green-600" },
      5: { label: t("user.passwordVeryStrong"), color: "text-green-700" },
    };

    setPasswordStrength({ score, ...strengthMap[score] });
  }, [password, t]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      form.reset();
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open, form]);

  const onSubmit = (values) => {
    const toastId = toast.loading(t("user.changingPassword"));

    changePassword(
      {
        id: user._id,
        password: values.password,
      },
      {
        onSuccess: (data) => {
          toast.success(data.message || t("user.passwordChanged"), {
            id: toastId,
          });
          form.reset();
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message || t("user.passwordChangeError"),
            {
              id: toastId,
            }
          );
        },
      }
    );
  };

  const getPasswordChecks = () => {
    if (!password) return [];
    
    return [
      {
        label: t("user.passwordLength"),
        passed: password.length >= 8,
      },
      {
        label: t("user.passwordLowercase"),
        passed: /[a-z]/.test(password),
      },
      {
        label: t("user.passwordUppercase"),
        passed: /[A-Z]/.test(password),
      },
      {
        label: t("user.passwordNumber"),
        passed: /\d/.test(password),
      },
    ];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="rounded-full bg-primary/10 p-2">
              <KeyRound className="h-5 w-5 text-primary" />
            </div>
            {t("user.changePassword")}
          </DialogTitle>
          <DialogDescription>
            {t("user.changePasswordDescription", {
              userName: user?.brandName || user?.fullName,
            })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("user.newPassword")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder={t("user.passwordPlaceholder")}
                        className={cn(
                          "pr-10",
                          password && passwordStrength.score > 0 && "border-l-4",
                          passwordStrength.score === 1 && "border-l-red-500",
                          passwordStrength.score === 2 && "border-l-red-500",
                          passwordStrength.score === 3 && "border-l-yellow-500",
                          passwordStrength.score === 4 && "border-l-green-500",
                          passwordStrength.score === 5 && "border-l-green-600"
                        )}
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  
                  {/* Password strength indicator */}
                  {password && (
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {t("user.passwordStrengthLabel")}:
                        </span>
                        <span className={cn("font-medium", passwordStrength.color)}>
                          {passwordStrength.label}
                        </span>
                      </div>
                      
                      {/* Strength bar */}
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={cn(
                              "h-1.5 flex-1 rounded-full transition-colors",
                              passwordStrength.score >= level
                                ? passwordStrength.score <= 2
                                  ? "bg-red-500"
                                  : passwordStrength.score === 3
                                  ? "bg-yellow-500"
                                  : "bg-green-500"
                                : "bg-gray-200"
                            )}
                          />
                        ))}
                      </div>

                      {/* Password requirements */}
                      <div className="space-y-1.5 pt-2">
                        {getPasswordChecks().map((check, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 text-xs"
                          >
                            {check.passed ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5 text-gray-300" />
                            )}
                            <span
                              className={cn(
                                check.passed
                                  ? "text-green-700"
                                  : "text-muted-foreground"
                              )}
                            >
                              {check.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("user.confirmPassword")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder={t("user.confirmPasswordPlaceholder")}
                        className={cn(
                          "pr-10",
                          confirmPassword &&
                            password === confirmPassword &&
                            "border-l-4 border-l-green-500"
                        )}
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  {confirmPassword && password === confirmPassword && (
                    <div className="flex items-center gap-1.5 text-xs text-green-700 mt-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>{t("user.passwordsMatch")}</span>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Warning alert */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {t("user.passwordChangeWarning")}
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t("main.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t("main.saving") : t("user.changePassword")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
